import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import ipfsUtils from '../utils/ipfsUtils';
import { evaluateKeyStrength, decryptJSON } from '../utils/encryption';
import contractUtils, { 
  connectWallet, 
  registerCase,
  getContract
} from '../utils/contractUtils';
import { 
  createNewCase, 
  addTransactionRecord, 
  addIpfsRecord 
} from '../utils/smallClaimsSchema';

/**
 * SmallClaimsIPFSBlockchainTest
 * 
 * Test script that demonstrates the complete workflow:
 * 1. Collecting form data with useState
 * 2. Saving to IPFS in both raw and encrypted formats
 * 3. Attaching IPFS hashes to Base Mainnet blockchain transactions
 * 4. Saving transaction data locally to a case file
 */
const SmallClaimsIPFSBlockchainTest = () => {
  // State for connection and loading
  const [account, setAccount] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [networkName, setNetworkName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // State for case form data
  const [caseData, setCaseData] = useState(createNewCase());
  
  // State for encryption
  const [encryptionKey, setEncryptionKey] = useState('');
  const [keyStrength, setKeyStrength] = useState(null);
  
  // State for IPFS upload results
  const [rawIpfsHash, setRawIpfsHash] = useState('');
  const [encryptedIpfsHash, setEncryptedIpfsHash] = useState('');
  
  // State for blockchain transaction
  const [txHash, setTxHash] = useState('');
  const [caseId, setCaseId] = useState(null);
  
  // State for decryption
  const [ipfsHashToDecrypt, setIpfsHashToDecrypt] = useState('');
  const [decryptionKey, setDecryptionKey] = useState('');
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [decryptedData, setDecryptedData] = useState(null);
  const [decryptionError, setDecryptionError] = useState('');
  const [decryptionMethod, setDecryptionMethod] = useState('ipfs-hash'); // 'ipfs-hash', 'ipfs-url', 'raw-json'
  const [encryptedJsonText, setEncryptedJsonText] = useState('');
  
  // State for test logs
  const [logs, setLogs] = useState([]);
  
  // Add to test logs
  const addLog = (message, type = 'info') => {
    setLogs(prevLogs => [
      ...prevLogs, 
      { 
        message, 
        type, 
        timestamp: new Date().toISOString() 
      }
    ]);
  };
  
  // Evaluate encryption key strength
  useEffect(() => {
    if (encryptionKey) {
      const strength = evaluateKeyStrength(encryptionKey);
      setKeyStrength(strength);
    } else {
      setKeyStrength(null);
    }
  }, [encryptionKey]);
  
  // Connect wallet
  const handleConnect = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      addLog('Connecting wallet...');
      const walletData = await connectWallet();
      const userAddress = walletData.address;
      setAccount(userAddress);
      setIsConnected(true);
      
      // Update plaintiff wallet address in case data
      setCaseData(prevData => ({
        ...prevData,
        plaintiff: {
          ...prevData.plaintiff,
          web3Identity: {
            ...prevData.plaintiff.web3Identity,
            walletAddress: userAddress
          }
        }
      }));
      
      // Get network information
      if (window.ethereum) {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const { chainId } = await provider.getNetwork();
        const network = contractUtils.getNetworkName(Number(chainId));
        setNetworkName(network);
      } else {
        setNetworkName("Not connected");
      }
      
      addLog(`Connected to wallet: ${userAddress} on ${networkName}`);
    } catch (err) {
      console.error("Connection error:", err);
      setError(err.message || "Failed to connect wallet");
      addLog(`Error connecting wallet: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle form input changes
  const handleInputChange = (section, field, value) => {
    setCaseData(prevData => {
      if (section) {
        return {
          ...prevData,
          [section]: {
            ...prevData[section],
            [field]: value
          }
        };
      } else {
        return {
          ...prevData,
          [field]: value
        };
      }
    });
    
    addLog(`Updated ${section ? section + '.' : ''}${field}: ${value}`);
  };
  
  // Upload to IPFS without encryption
  const uploadToIPFS = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      addLog('Uploading case data to IPFS (unencrypted)...');
      
      // Add metadata
      const dataWithMetadata = {
        ...caseData,
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
          format: 'SmallClaimsCase'
        }
      };
      
      // Upload to IPFS
      const cid = await ipfsUtils.uploadToIPFS(dataWithMetadata);
      setRawIpfsHash(cid);
      
      // Update case data with IPFS record
      setCaseData(prevData => 
        addIpfsRecord(prevData, cid, 'Initial case filing', 'filing')
      );
      
      addLog(`Case data uploaded to IPFS: ${cid}`, 'success');
      setSuccessMessage(`Raw data uploaded to IPFS: ${cid}`);
    } catch (err) {
      console.error("IPFS upload error:", err);
      setError(err.message || "Failed to upload to IPFS");
      addLog(`Error uploading to IPFS: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Upload to IPFS with encryption
  const uploadEncryptedToIPFS = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Validate encryption key
      if (!encryptionKey || encryptionKey.length < 4) {
        throw new Error("Please provide a valid encryption key (at least 4 characters)");
      }
      
      addLog('Encrypting and uploading case data to IPFS...');
      
      // Add metadata
      const dataWithMetadata = {
        ...caseData,
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
          format: 'SmallClaimsCase',
          encryptionMethod: 'AES',
          keyStrength: keyStrength?.level || 0
        }
      };
      
      // Encrypt and upload to IPFS
      const cid = await ipfsUtils.encryptAndUploadToIPFS(dataWithMetadata, encryptionKey);
      setEncryptedIpfsHash(cid);
      
      // Update case data with IPFS record
      setCaseData(prevData => 
        addIpfsRecord(prevData, cid, 'Encrypted case filing', 'encrypted-filing')
      );
      
      addLog(`Encrypted case data uploaded to IPFS: ${cid}`, 'success');
      setSuccessMessage(`Encrypted data uploaded to IPFS: ${cid}`);
    } catch (err) {
      console.error("Encrypted IPFS upload error:", err);
      setError(err.message || "Failed to encrypt and upload to IPFS");
      addLog(`Error encrypting and uploading to IPFS: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Register case on blockchain
  const registerCaseOnBlockchain = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      if (!isConnected) {
        throw new Error("You must connect your wallet with MetaMask to perform blockchain transactions");
      }
      
      if (!rawIpfsHash && !encryptedIpfsHash) {
        throw new Error("Please upload case data to IPFS first");
      }
      
      // Use the encrypted hash if available, otherwise use the raw hash
      const ipfsHash = encryptedIpfsHash || rawIpfsHash;
      if (!ipfsHash) {
        throw new Error("No IPFS hash available");
      }
      
      addLog('Registering case on Base Mainnet blockchain...');
      
      // Format IPFS hash as URI
      const ipfsUri = `ipfs://${ipfsHash}`;
      
      // Get values from case data
      const initialStatus = caseData.currentStatus || 'Filed';
      const plaintiffAddress = caseData.plaintiff.web3Identity.walletAddress || account;
      const defendantAddress = caseData.defendant.web3Identity.walletAddress || ethers.ZeroAddress;
      
      // Ensure we're on Base Mainnet
      addLog('Attempting to switch to Base Mainnet if needed...');
      await contractUtils.switchToBaseMainnet();
      addLog('Successfully connected to Base Mainnet');
      
      // Check if the account has the REGISTRAR_ROLE
      const contract = await getContract();
      const registrarRole = await contract.REGISTRAR_ROLE();
      const hasRegistrarRole = await contract.hasRole(registrarRole, account);
      
      if (!hasRegistrarRole) {
        addLog(`Your account ${account} does not have the REGISTRAR_ROLE`, 'error');
        throw new Error(`Your wallet address ${account} does not have permission to register cases. You need the REGISTRAR_ROLE assigned to your address.`);
      }
      
      addLog(`Verified REGISTRAR_ROLE for account ${account}`, 'success');
      
      // Attempt to get the filing fee
      const filingFee = await contract.filingFee();
      addLog(`Filing fee required: ${ethers.formatEther(filingFee)} ETH`, 'info');
      
      // Real blockchain interaction - with detailed logging
      addLog('Attempting to register case on blockchain with the following parameters:', 'info');
      addLog(`Contract address: ${contractUtils.CONTRACT_ADDRESSES.registry}`, 'info');
      addLog(`IPFS URI: ${ipfsUri}`, 'info');
      addLog(`Status: ${initialStatus}`, 'info');
      addLog(`Plaintiff: ${plaintiffAddress}`, 'info');
      addLog(`Defendant: ${defendantAddress || ethers.ZeroAddress}`, 'info');
      addLog(`Filing Fee: ${ethers.formatEther(filingFee)} ETH`, 'info');
      
      // Get wallet balance to ensure sufficient funds
      const provider = await contractUtils.getProvider();
      const balance = await provider.getBalance(account);
      
      addLog(`Wallet balance: ${ethers.formatEther(balance)} ETH`, 'info');
      
      if (balance < filingFee) {
        throw new Error(`Insufficient funds for filing fee. You have ${ethers.formatEther(balance)} ETH but need at least ${ethers.formatEther(filingFee)} ETH.`);
      }
      
      // Attempt actual registration with explicit filing fee
      const result = await registerCase(
        ipfsUri,
        initialStatus,
        plaintiffAddress,
        defendantAddress || ethers.ZeroAddress,
        { value: filingFee } // Explicitly send the filing fee
      );
      
      // Update state with transaction info
      setTxHash(result.receipt.hash);
      setCaseId(result.tokenId);
      
      // Update case data with transaction record
      const updatedCaseData = addTransactionRecord(
        caseData,
        result.receipt.hash,
        '8453', // Base Mainnet chain ID
        'register',
        'Initial case registration'
      );
      
      // Add case ID to case data
      updatedCaseData.caseId = `SC-${result.tokenId}`;
      updatedCaseData.caseNumber = `SC-${result.tokenId}`;
      setCaseData(updatedCaseData);
      
      addLog(`Case registered on blockchain. Transaction hash: ${result.receipt.hash}`, 'success');
      addLog(`Case ID assigned: ${result.tokenId}`, 'success');
      setSuccessMessage(`Case registered on blockchain. Case ID: ${result.tokenId}`);
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to register case on blockchain");
      addLog(`Error registering case: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save case data locally
  const saveCaseDataLocally = () => {
    try {
      addLog('Saving case data locally...');
      
      // Convert case data to JSON string
      const jsonData = JSON.stringify(caseData, null, 2);
      
      // Create blob and download link
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `small-claims-case-${caseData.caseId || 'draft'}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      addLog('Case data saved locally', 'success');
      setSuccessMessage('Case data saved locally to file');
    } catch (err) {
      console.error("Error saving locally:", err);
      setError(err.message || "Failed to save case data locally");
      addLog(`Error saving locally: ${err.message}`, 'error');
    }
  };
  
  // Decrypt IPFS data
  const decryptIpfsData = async () => {
    setIsDecrypting(true);
    setDecryptionError('');
    setDecryptedData(null);
    
    try {
      if (!decryptionKey) {
        throw new Error("Please enter a decryption key");
      }
      
      let encryptedData;
      
      // Handle different decryption methods
      if (decryptionMethod === 'ipfs-hash') {
        // Method 1: Decrypt using IPFS hash directly
        if (!ipfsHashToDecrypt) {
          throw new Error("Please enter an IPFS hash to decrypt");
        }
        
        addLog(`Fetching encrypted data from IPFS using hash: ${ipfsHashToDecrypt}...`);
        
        try {
          encryptedData = await ipfsUtils.getFromIPFS(ipfsHashToDecrypt);
          addLog('Encrypted data retrieved from IPFS', 'success');
        } catch (fetchError) {
          addLog(`Error fetching from IPFS: ${fetchError.message}`, 'error');
          throw new Error(`Failed to fetch data from IPFS: ${fetchError.message}`);
        }
      } 
      else if (decryptionMethod === 'ipfs-url') {
        // Method 2: Decrypt using IPFS URL
        if (!ipfsHashToDecrypt) {
          throw new Error("Please enter an IPFS URL to decrypt");
        }
        
        // Extract the IPFS hash from the URL
        let ipfsHash;
        try {
          // Handle different gateway URL formats
          if (ipfsHashToDecrypt.includes('/ipfs/')) {
            ipfsHash = ipfsHashToDecrypt.split('/ipfs/')[1];
          } else {
            throw new Error("Invalid IPFS URL format. URL should contain '/ipfs/' followed by the hash");
          }
          
          // Remove any query parameters or trailing slashes
          ipfsHash = ipfsHash.split('?')[0].replace(/\/$/, '');
          
          addLog(`Extracted IPFS hash from URL: ${ipfsHash}`);
          addLog(`Fetching encrypted data from IPFS...`);
          
          encryptedData = await ipfsUtils.getFromIPFS(ipfsHash);
          addLog('Encrypted data retrieved from IPFS', 'success');
        } catch (fetchError) {
          addLog(`Error processing IPFS URL: ${fetchError.message}`, 'error');
          throw new Error(`Failed to fetch data from IPFS URL: ${fetchError.message}`);
        }
      } 
      else if (decryptionMethod === 'raw-json') {
        // Method 3: Decrypt raw JSON text
        if (!encryptedJsonText) {
          throw new Error("Please enter the encrypted JSON text to decrypt");
        }
        
        addLog('Processing provided encrypted JSON text...');
        
        // Parse the JSON text
        try {
          encryptedData = JSON.parse(encryptedJsonText);
          addLog('Successfully parsed encrypted JSON text', 'success');
        } catch (parseError) {
          addLog(`Error parsing JSON: ${parseError.message}`, 'error');
          throw new Error(`Invalid JSON format: ${parseError.message}`);
        }
      }
      
      // Check if we received valid data
      if (!encryptedData) {
        throw new Error("No encrypted data received to decrypt");
      }
      
      // If it's not a string and doesn't have the expected properties, it might be already decrypted
      if (typeof encryptedData !== 'string' && !encryptedData.data && !encryptedData.success) {
        addLog('Data appears to be already unencrypted', 'info');
        setDecryptedData(encryptedData);
        setSuccessMessage('Data was already unencrypted');
        return;
      }
      
      // Attempt to decrypt the data
      addLog('Attempting to decrypt data...');
      
      // Handle string or object format
      const dataToDecrypt = typeof encryptedData === 'string' 
        ? encryptedData 
        : (encryptedData.data || encryptedData);
      
      const decrypted = decryptJSON(dataToDecrypt, decryptionKey);
      
      if (!decrypted.success) {
        throw new Error(`Decryption failed: ${decrypted.error}`);
      }
      
      // Store the decrypted data
      setDecryptedData(decrypted.data);
      addLog('Data successfully decrypted!', 'success');
      setSuccessMessage('Data successfully decrypted!');
      
      // Set encryption key for potential future uploads
      setEncryptionKey(decryptionKey);
    } catch (err) {
      console.error("Decryption error:", err);
      setDecryptionError(err.message || "Failed to decrypt data");
      addLog(`Error decrypting data: ${err.message}`, 'error');
    } finally {
      setIsDecrypting(false);
    }
  };
  
  // Save decrypted data locally
  const saveDecryptedDataLocally = () => {
    try {
      if (!decryptedData) {
        throw new Error("No decrypted data available");
      }
      
      addLog('Saving decrypted data locally...');
      
      // Convert data to JSON string
      const jsonData = JSON.stringify(decryptedData, null, 2);
      
      // Create blob and download link
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `decrypted-${ipfsHashToDecrypt.slice(0, 8)}-${new Date().getTime()}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      addLog('Decrypted data saved locally', 'success');
      setSuccessMessage('Decrypted data saved locally to file');
    } catch (err) {
      console.error("Error saving decrypted data:", err);
      setError(err.message || "Failed to save decrypted data locally");
      addLog(`Error saving decrypted data: ${err.message}`, 'error');
    }
  };
  
  // Run full test process
  const runFullProcess = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Step 1: Connect wallet if not connected
      if (!isConnected) {
        try {
          await handleConnect();
        } catch (connectError) {
          addLog(`Wallet connection failed: ${connectError.message}. Continuing in test mode...`, 'warning');
          // Continue in test mode even if wallet connection fails
        }
      }
      
      // Step 2: Fill case data with test data
      const testData = {
        ...caseData,
        plaintiff: {
          ...caseData.plaintiff,
          name: "John Doe",
          type: "individual",
          address: {
            street: "123 Main St",
            city: "San Francisco",
            state: "CA",
            zipCode: "94105"
          },
          contact: {
            phone: "555-123-4567",
            email: "john.doe@example.com"
          },
          web3Identity: {
            ...caseData.plaintiff.web3Identity,
            walletAddress: account || "0x1234567890123456789012345678901234567890"
          }
        },
        defendant: {
          ...caseData.defendant,
          name: "ABC Company",
          type: "business",
          address: {
            street: "456 Market St",
            city: "San Francisco",
            state: "CA",
            zipCode: "94105"
          }
        },
        claim: {
          ...caseData.claim,
          amount: 5000,
          type: "breach_of_contract",
          description: "Failure to deliver contracted services as specified in the agreement dated 2023-01-15",
          incidentDate: "2023-01-30"
        },
        venue: {
          ...caseData.venue,
          county: "san_francisco",
          courthouse: "San Francisco Superior Court",
          department: "Civil Claims Division"
        }
      };
      
      setCaseData(testData);
      addLog('Populated case with test data');
      
      // Step 3: Generate encryption key
      const autoEncryptionKey = `SC-${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
      setEncryptionKey(autoEncryptionKey);
      addLog(`Generated encryption key: ${autoEncryptionKey}`);
      
      // Step 4: Upload to IPFS (raw)
      try {
        await uploadToIPFS();
      } catch (ipfsError) {
        addLog(`IPFS raw upload failed: ${ipfsError.message}. Simulating...`, 'warning');
        // Simulate IPFS upload if it fails
        const mockCid = 'QmHash' + Math.random().toString(36).substring(2, 15);
        setRawIpfsHash(mockCid);
        setCaseData(prevData => 
          addIpfsRecord(prevData, mockCid, 'Simulated raw case filing', 'filing-test')
        );
        addLog(`Simulated IPFS CID generated: ${mockCid}`, 'info');
      }
      
      // Step 5: Upload to IPFS (encrypted)
      try {
        await uploadEncryptedToIPFS();
      } catch (encryptError) {
        addLog(`IPFS encrypted upload failed: ${encryptError.message}. Simulating...`, 'warning');
        // Simulate encrypted IPFS upload if it fails
        const mockEncryptedCid = 'QmEnc' + Math.random().toString(36).substring(2, 15);
        setEncryptedIpfsHash(mockEncryptedCid);
        setCaseData(prevData => 
          addIpfsRecord(prevData, mockEncryptedCid, 'Simulated encrypted case filing', 'encrypted-filing-test')
        );
        addLog(`Simulated encrypted IPFS CID generated: ${mockEncryptedCid}`, 'info');
      }
      
      // Step 6: Register on blockchain
      try {
        await registerCaseOnBlockchain();
      } catch (blockchainError) {
        addLog(`Blockchain registration failed completely: ${blockchainError.message}`, 'error');
        // The function already has fallback simulation
      }
      
      // Step 7: Save locally
      saveCaseDataLocally();
      
      setSuccessMessage('Full process completed! (Some steps may have run in test mode)');
    } catch (err) {
      console.error("Process error:", err);
      setError(err.message || "Process failed");
      addLog(`Error in process: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Render the test UI
  return (
    <div className="small-claims-ipfs-blockchain-test">
      <h1>Civil Claims IPFS & Blockchain Integration Test</h1>
      
      <section className="wallet-section">
        <h2>1. Wallet Connection</h2>
        {!isConnected ? (
          <button 
            className="btn btn-primary" 
            onClick={handleConnect}
            disabled={isLoading}
          >
            Connect Wallet
          </button>
        ) : (
          <div className="connection-info">
            <p><strong>Connected Account:</strong> {account}</p>
            <p><strong>Network:</strong> {networkName}</p>
          </div>
        )}
      </section>
      
      <section className="case-data-section">
        <h2>2. Case Data (with useState)</h2>
        <div className="form-group">
          <label>Plaintiff Name:</label>
          <input 
            type="text"
            value={caseData.plaintiff.name}
            onChange={(e) => handleInputChange('plaintiff', 'name', e.target.value)}
            placeholder="Enter plaintiff name"
          />
        </div>
        
        <div className="form-group">
          <label>Defendant Name:</label>
          <input 
            type="text"
            value={caseData.defendant.name}
            onChange={(e) => handleInputChange('defendant', 'name', e.target.value)}
            placeholder="Enter defendant name"
          />
        </div>
        
        <div className="form-group">
          <label>Claim Amount:</label>
          <input 
            type="number"
            value={caseData.claim.amount || ''}
            onChange={(e) => handleInputChange('claim', 'amount', parseFloat(e.target.value) || 0)}
            placeholder="Enter claim amount"
          />
        </div>
        
        <div className="form-group">
          <label>Claim Description:</label>
          <textarea 
            value={caseData.claim.description}
            onChange={(e) => handleInputChange('claim', 'description', e.target.value)}
            placeholder="Describe the claim"
            rows="4"
          />
        </div>
      </section>
      
      <section className="encryption-section">
        <h2>3. Encryption Key</h2>
        <div className="form-group">
          <label>Encryption Key:</label>
          <input 
            type="text"
            value={encryptionKey}
            onChange={(e) => setEncryptionKey(e.target.value)}
            placeholder="Enter encryption key"
          />
        </div>
        
        {keyStrength && (
          <div className={`key-strength key-strength-${keyStrength.level}`}>
            <p>Key Strength: {keyStrength.strengthText}</p>
            {keyStrength.feedback && <p>Recommendation: {keyStrength.feedback}</p>}
          </div>
        )}
      </section>
      
      <section className="ipfs-section">
        <h2>4. IPFS Upload</h2>
        <div className="button-group">
          <button 
            className="btn btn-primary"
            onClick={uploadToIPFS}
            disabled={isLoading}
          >
            Upload Raw Data to IPFS
          </button>
          
          <button 
            className="btn btn-primary"
            onClick={uploadEncryptedToIPFS}
            disabled={isLoading || !encryptionKey}
          >
            Upload Encrypted Data to IPFS
          </button>
        </div>
        
        {rawIpfsHash && (
          <div className="ipfs-result">
            <p><strong>Raw IPFS Hash:</strong> {rawIpfsHash}</p>
            <p><a href={ipfsUtils.getIPFSGatewayUrl(rawIpfsHash)} target="_blank" rel="noopener noreferrer">
              View on IPFS Gateway
            </a></p>
          </div>
        )}
        
        {encryptedIpfsHash && (
          <div className="ipfs-result">
            <p><strong>Encrypted IPFS Hash:</strong> {encryptedIpfsHash}</p>
            <p><a href={ipfsUtils.getIPFSGatewayUrl(encryptedIpfsHash)} target="_blank" rel="noopener noreferrer">
              View on IPFS Gateway
            </a></p>
          </div>
        )}
      </section>
      
      <section className="blockchain-section">
        <h2>5. Blockchain Registration</h2>
        
        {!isConnected && (
          <div className="warning-notice">
            <p>⚠️ You must connect your wallet with MetaMask to perform blockchain transactions.</p>
          </div>
        )}
        
        <div className="requirements-notice">
          <h4>Requirements for blockchain registration:</h4>
          <ul>
            <li>Connected MetaMask wallet on Base Mainnet</li>
            <li>The REGISTRAR_ROLE assigned to your wallet address</li>
            <li>Sufficient ETH for filing fee (check contract for current fee)</li>
            <li>IPFS hash from previous step (raw or encrypted)</li>
          </ul>
        </div>
        
        <button 
          className="btn btn-primary"
          onClick={registerCaseOnBlockchain}
          disabled={isLoading || (!rawIpfsHash && !encryptedIpfsHash) || !isConnected}
        >
          Register Case on Blockchain
        </button>
        
        {txHash && (
          <div className="blockchain-result">
            <p><strong>Transaction Hash:</strong> {txHash}</p>
            <p><a href={contractUtils.getExplorerUrl(txHash)} target="_blank" rel="noopener noreferrer">
              View on BaseScan
            </a></p>
          </div>
        )}
        
        {caseId && (
          <div className="blockchain-result">
            <p><strong>Case ID:</strong> {caseId}</p>
          </div>
        )}
      </section>
      
      <section className="local-save-section">
        <h2>6. Save Case File Locally</h2>
        <button 
          className="btn btn-primary"
          onClick={saveCaseDataLocally}
          disabled={isLoading}
        >
          Save Case Data Locally
        </button>
      </section>
      
      <section className="decryption-section">
        <h2>7. Decrypt IPFS Data</h2>
        
        <div className="decryption-methods">
          <div className="method-selector">
            <label>Decryption Method:</label>
            <div className="method-options">
              <label className={`method-option ${decryptionMethod === 'ipfs-hash' ? 'selected' : ''}`}>
                <input 
                  type="radio"
                  name="decryptionMethod"
                  value="ipfs-hash"
                  checked={decryptionMethod === 'ipfs-hash'}
                  onChange={() => setDecryptionMethod('ipfs-hash')}
                />
                IPFS Hash Only
              </label>
              <label className={`method-option ${decryptionMethod === 'ipfs-url' ? 'selected' : ''}`}>
                <input 
                  type="radio"
                  name="decryptionMethod"
                  value="ipfs-url"
                  checked={decryptionMethod === 'ipfs-url'}
                  onChange={() => setDecryptionMethod('ipfs-url')}
                />
                IPFS Gateway URL
              </label>
              <label className={`method-option ${decryptionMethod === 'raw-json' ? 'selected' : ''}`}>
                <input 
                  type="radio"
                  name="decryptionMethod"
                  value="raw-json"
                  checked={decryptionMethod === 'raw-json'}
                  onChange={() => setDecryptionMethod('raw-json')}
                />
                Raw JSON Text
              </label>
            </div>
          </div>
        </div>
        
        {/* Different input fields based on method */}
        {(decryptionMethod === 'ipfs-hash' || decryptionMethod === 'ipfs-url') && (
          <div className="form-group">
            <label>
              {decryptionMethod === 'ipfs-hash' ? 'IPFS Hash to Decrypt:' : 'IPFS Gateway URL:'}
              {decryptionMethod === 'ipfs-hash' && (
                <small className="input-help">Enter only the IPFS hash (e.g., "QmYjtig7...")</small>
              )}
              {decryptionMethod === 'ipfs-url' && (
                <small className="input-help">Enter complete URL (e.g., "https://freeweb3.infura-ipfs.io/ipfs/QmYjtig7...")</small>
              )}
            </label>
            <input 
              type="text"
              value={ipfsHashToDecrypt}
              onChange={(e) => setIpfsHashToDecrypt(e.target.value)}
              placeholder={decryptionMethod === 'ipfs-hash' ? 'Enter IPFS hash' : 'Enter IPFS gateway URL'}
            />
            {encryptedIpfsHash && !ipfsHashToDecrypt && (
              <button 
                className="btn btn-sm"
                onClick={() => {
                  if (decryptionMethod === 'ipfs-hash') {
                    setIpfsHashToDecrypt(encryptedIpfsHash);
                  } else {
                    setIpfsHashToDecrypt(`https://freeweb3.infura-ipfs.io/ipfs/${encryptedIpfsHash}`);
                  }
                  setDecryptionKey(encryptionKey);
                }}
              >
                Use Current Encryption Values
              </button>
            )}
          </div>
        )}
        
        {decryptionMethod === 'raw-json' && (
          <div className="form-group">
            <label>Encrypted JSON Text:</label>
            <textarea 
              value={encryptedJsonText}
              onChange={(e) => setEncryptedJsonText(e.target.value)}
              placeholder="Paste the encrypted JSON data here"
              rows="8"
            />
          </div>
        )}
        
        <div className="form-group">
          <label>Decryption Key:</label>
          <input 
            type="text"
            value={decryptionKey}
            onChange={(e) => setDecryptionKey(e.target.value)}
            placeholder="Enter decryption key"
          />
          {encryptionKey && !decryptionKey && (
            <button 
              className="btn btn-sm"
              onClick={() => setDecryptionKey(encryptionKey)}
            >
              Use Current Key
            </button>
          )}
        </div>
        
        <div className="button-group">
          <button 
            className="btn btn-primary"
            onClick={decryptIpfsData}
            disabled={isDecrypting || 
              (decryptionMethod === 'ipfs-hash' && !ipfsHashToDecrypt) || 
              (decryptionMethod === 'ipfs-url' && !ipfsHashToDecrypt) || 
              (decryptionMethod === 'raw-json' && !encryptedJsonText) || 
              !decryptionKey}
          >
            {isDecrypting ? 'Decrypting...' : 'Decrypt Data'}
          </button>
          
          {decryptedData && (
            <button 
              className="btn btn-primary"
              onClick={saveDecryptedDataLocally}
              disabled={isDecrypting}
            >
              Save Decrypted Data
            </button>
          )}
        </div>
        
        {decryptionError && (
          <div className="error-message">
            <p>{decryptionError}</p>
          </div>
        )}
        
        {decryptedData && (
          <div className="decryption-result">
            <h4>Decrypted Data Preview:</h4>
            <div className="data-preview">
              <pre>
                {JSON.stringify(decryptedData, null, 2).slice(0, 300)}
                {JSON.stringify(decryptedData, null, 2).length > 300 ? '...' : ''}
              </pre>
            </div>
          </div>
        )}
      </section>
      
      <section className="full-process-section">
        <h2>8. Run Full Process</h2>
        <button 
          className="btn btn-success"
          onClick={runFullProcess}
          disabled={isLoading}
        >
          Run Complete Test Process
        </button>
      </section>
      
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      )}
      
      <section className="logs-section">
        <h2>Test Logs</h2>
        <div className="logs">
          {logs.map((log, index) => (
            <div key={index} className={`log log-${log.type}`}>
              <span className="log-timestamp">{new Date(log.timestamp).toLocaleTimeString()}</span>
              <span className="log-message">{log.message}</span>
            </div>
          ))}
          {logs.length === 0 && <p>No logs yet. Start the test process to see logs here.</p>}
        </div>
      </section>
      
      <style>{`
        .small-claims-ipfs-blockchain-test {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          font-family: Arial, sans-serif;
        }
        
        h1, h2 {
          color: #333;
        }
        
        section {
          margin-bottom: 30px;
          padding: 20px;
          background: #f9f9f9;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .form-group {
          margin-bottom: 15px;
        }
        
        label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }
        
        input, textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .btn {
          padding: 10px 15px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          margin-right: 10px;
          margin-bottom: 10px;
        }
        
        .btn-primary {
          background: #0066cc;
          color: white;
        }
        
        .btn-success {
          background: #28a745;
          color: white;
        }
        
        .btn:disabled {
          background: #cccccc;
          cursor: not-allowed;
        }
        
        .button-group {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 15px;
        }
        
        .connection-info, .ipfs-result, .blockchain-result {
          margin-top: 15px;
          padding: 10px;
          background: #e9f7fe;
          border-radius: 4px;
          border-left: 4px solid #0066cc;
        }
        
        .error-message {
          padding: 15px;
          background: #ffeeee;
          color: #d9534f;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        
        .success-message {
          padding: 15px;
          background: #f0fff0;
          color: #5cb85c;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        
        .key-strength {
          margin-top: 10px;
          padding: 10px;
          border-radius: 4px;
        }
        
        .key-strength-1 { background: #ffeeee; color: #d9534f; }
        .key-strength-2 { background: #fff3cd; color: #856404; }
        .key-strength-3 { background: #fff3cd; color: #856404; }
        .key-strength-4 { background: #d4edda; color: #155724; }
        .key-strength-5 { background: #d4edda; color: #155724; }
        
        .logs {
          max-height: 300px;
          overflow-y: auto;
          background: #f5f5f5;
          padding: 10px;
          border-radius: 4px;
          font-family: monospace;
        }
        
        .log {
          margin-bottom: 5px;
          padding: 5px;
          border-radius: 3px;
        }
        
        .log-info { background: #e9f7fe; }
        .log-success { background: #d4edda; }
        .log-error { background: #ffeeee; }
        
        .log-timestamp {
          color: #666;
          margin-right: 10px;
        }
        
        .test-mode-notice {
          background: #fff3cd;
          color: #856404;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
          border-left: 4px solid #ffc107;
        }
        
        .warning-notice {
          background: #f8d7da;
          color: #721c24;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
          border-left: 4px solid #dc3545;
        }
        
        .requirements-notice {
          background: #d1ecf1;
          color: #0c5460;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
          border-left: 4px solid #17a2b8;
        }
        
        .requirements-notice h4 {
          margin-top: 0;
          margin-bottom: 10px;
          font-size: 16px;
        }
        
        .requirements-notice ul {
          margin: 0;
          padding-left: 20px;
        }
        
        .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
          margin-left: 10px;
        }
        
        .decryption-result {
          margin-top: 20px;
          padding: 15px;
          background: #f5fbff;
          border-radius: 4px;
          border-left: 4px solid #0066cc;
        }
        
        .data-preview {
          margin-top: 10px;
          background: #f5f5f5;
          padding: 10px;
          border-radius: 4px;
          overflow: auto;
          max-height: 200px;
        }
        
        pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-all;
          font-size: 12px;
        }
        
        .decryption-methods {
          margin-bottom: 20px;
        }
        
        .method-selector label {
          margin-bottom: 10px;
          font-weight: bold;
        }
        
        .method-options {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .method-option {
          display: flex;
          align-items: center;
          gap: 5px;
          background: #f5f5f5;
          padding: 8px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: normal;
        }
        
        .method-option:hover {
          background: #e9e9e9;
        }
        
        .method-option.selected {
          background: #e6f2ff;
          border: 1px solid #0066cc;
        }
        
        .input-help {
          display: block;
          font-weight: normal;
          font-size: 12px;
          color: #666;
          margin-top: 2px;
        }
        
        textarea {
          width: 100%;
          min-height: 100px;
          font-family: monospace;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
};

export default SmallClaimsIPFSBlockchainTest;